/// <amd-module name="Repos/Views/PullRequest/Discussion/PullRequestDiscussionIntegrationService" />
import * as React from "react";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext, GitPullRequestCommentThreadContext } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IChangesTreeDiscussionIntegration,
    IDiffMonacoDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    IDiscussionIntegrationService,
    IDiscussionThreadCommonProps,
    ILineInfo,
    LineInfoType
} from "Repos/Discussion/Common/DiscussionContracts";
import { IDiscussionIntegrationContext, IDiscussionIntegrationProps } from "Repos/Discussion/Editor/Integrations/BaseDiscussionIntegration";
import { ChangesTreeDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/ChangesTreeDiscussionIntegration";
import { DiffSummaryDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/DiffSummaryDiscussionIntegration";
import { MonacoDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/MonacoDiscussionIntegration";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ITreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { hasPermission, PullRequestPermissions } from "../PullRequestPermissionService";
import { getPullRequestViewService } from "../PullRequestViewService";
import { PullRequestDiscussion } from "./Components/PullRequestDiscussion";
import { getPullRequestDiscussionService, IGitPullRequestCommentThreadModel } from "./PullRequestDiscussionThreadService";
import { newPullRequestThreadModel } from "./PullRequestDiscussionUtil";
import { IEditorSelection } from "Repos/Common/Contracts/Editor";

export interface IPullRequestDiscussionIntegrationService extends IDiscussionIntegrationService, IVssService {}

class PullRequestDiscussionIntegrationService extends VssService implements IPullRequestDiscussionIntegrationService {
    public getMonacoIntegration(
        filePath: string,
        diffOrientation: IReadonlyObservableValue<DiffViewerOrientation>, 
        getCodeSelectionLinkUrl: (selection: IEditorSelection, editorType?: number, diffOrientation?: DiffViewerOrientation) => string
    ): IDiffMonacoDiscussionIntegration {
        return new MonacoDiscussionIntegration({ ...this.getIntegrationProps(filePath, diffOrientation), getCodeSelectionLinkUrl });
    }

    public getDiffSummaryIntegrations(
        filePaths: string[],
        diffOrientation: IReadonlyObservableValue<DiffViewerOrientation>
    ): IDiffSummaryDiscussionIntegration[] {
        return filePaths.map(filePath => new DiffSummaryDiscussionIntegration({ ...this.getIntegrationProps(filePath, diffOrientation) }));
    }

    public getChangesTreeIntegration(
        fileItemsProvider: ITreeItemProvider<IChange>,
        onThreadsUpdated?: () => void
    ): IChangesTreeDiscussionIntegration<ITreeItemProvider<IChange>> {
        return new ChangesTreeDiscussionIntegration({ ...this.getIntegrationProps("/"), fileItemsProvider, onThreadsUpdated });
    }

    public getRootDiffSummaryIntegration(): IDiffSummaryDiscussionIntegration | undefined {
        // PR details already has overview page to display root level discusssions
        return undefined;
    }

    private extractFilePath = (threadModel: IGitPullRequestCommentThreadModel): string | undefined => {
        if (threadModel.thread.threadContext) {
            return threadModel.thread.threadContext.filePath;
        }
    };

    private filterThreads = (threads: IGitPullRequestCommentThreadModel[], filePath: string): IGitPullRequestCommentThreadModel[] => {
        return threads.filter(tm => tm.thread.threadContext && equals(tm.thread.threadContext.filePath, filePath, true));
    };

    private getIntegrationContext(threadModel: IGitPullRequestCommentThreadModel): IDiscussionIntegrationContext {
        const { leftFileEnd, leftFileStart, rightFileEnd, rightFileStart } = threadModel.thread.threadContext;
        const addToOriginalDiff = !!leftFileStart || !!leftFileEnd;

        let lineInfo: ILineInfo = { lineStart: -1, lineStartOffset: 0, lineEnd: -1, lineEndOffset: 0, type: LineInfoType.Actual };
        if (leftFileStart || leftFileEnd || rightFileStart || rightFileEnd) {
            lineInfo = {
                lineEnd: addToOriginalDiff ? leftFileEnd.line : rightFileEnd.line,
                lineEndOffset: addToOriginalDiff ? leftFileEnd.offset : rightFileEnd.offset,
                lineStart: addToOriginalDiff ? leftFileStart.line : rightFileStart.line,
                lineStartOffset: addToOriginalDiff ? leftFileStart.offset : rightFileStart.offset,
                type: LineInfoType.Actual
            };
        }

        return { addToOriginalDiff, lineInfo };
    }

    private getIntegrationProps(filePath: string, diffOrientation?: IReadonlyObservableValue<DiffViewerOrientation>): IDiscussionIntegrationProps {
        const { pageContext } = this;
        const discussionService = getPullRequestDiscussionService(pageContext);
        const discussionThreadContext = discussionService.getThreadContext();

        return {
            currentUser: discussionThreadContext.currentUser,
            canAddComments: hasPermission(pageContext, PullRequestPermissions.AddEditComment),
            createThreadModel: newPullRequestThreadModel,
            diffOrientation,
            discussionStore: discussionService,
            extractFilePath: this.extractFilePath,
            filePath,
            filterThreads: this.filterThreads,
            getIntegrationContext: this.getIntegrationContext,
            getLineNumber: this.getLineNumber,
            initializeThread: this.initializeThread,
            isOriginalSide: this.isOriginalSide,
            pageContext,
            renderDiscussion: (props: IDiscussionThreadCommonProps) => (
                <PullRequestDiscussion
                    author={discussionThreadContext.currentUser}
                    className={props.className}
                    onCancelThread={props.onCancelThread}
                    onChangeStatus={status => {
                        props.onChangeStatus && props.onChangeStatus(status);
                        discussionService.updateThreadStatus();
                    }}
                    onDeleteThread={props.onDeleteThread}
                    onPublishThread={() => discussionService.updateThreadStatus()}
                    permissions={discussionThreadContext.permissions}
                    personaProvider={discussionThreadContext.personaProvider}
                    pullRequestId={discussionThreadContext.pullRequestId}
                    repository={discussionThreadContext.repository}
                    thread={props.thread as IGitPullRequestCommentThreadModel}
                />
            )
        };
    }

    private getLineNumber = (threadModel: IGitPullRequestCommentThreadModel): number | undefined => {
        if (threadModel.thread.threadContext) {
            if (threadModel.thread.threadContext.leftFileEnd) {
                return threadModel.thread.threadContext.leftFileEnd.line;
            }

            if (threadModel.thread.threadContext.rightFileEnd) {
                return threadModel.thread.threadContext.rightFileEnd.line;
            }
        }
    };

    private initializeThread = (
        threadModel: IGitPullRequestCommentThreadModel,
        filePath: string,
        integrationContext: IDiscussionIntegrationContext
    ): void => {
        const { addToOriginalDiff, lineInfo } = integrationContext;
        const thread = threadModel.thread;
        thread.threadContext = { filePath } as CommentThreadContext;
        if (addToOriginalDiff) {
            thread.threadContext.leftFileEnd = { line: lineInfo ? lineInfo.lineEnd : 0, offset: lineInfo ? lineInfo.lineEndOffset : 0 };
            thread.threadContext.leftFileStart = { line: lineInfo ? lineInfo.lineStart : 0, offset: lineInfo ? lineInfo.lineStartOffset : 0 };
        } else {
            thread.threadContext.rightFileEnd = { line: lineInfo ? lineInfo.lineEnd : 0, offset: lineInfo ? lineInfo.lineEndOffset : 0 };
            thread.threadContext.rightFileStart = { line: lineInfo ? lineInfo.lineStart : 0, offset: lineInfo ? lineInfo.lineStartOffset : 0 };
        }

        // Add iteration context to the thread
        const viewService = getPullRequestViewService(this.pageContext);

        const currentIterationId = viewService.getSelectedIterationId().value;
        const baseIterationId = viewService.getBaseIterationId().value || currentIterationId;

        const changes = viewService.getChanges().value;
        const fileChange = changes.changesByPath[filePath];

        thread.pullRequestThreadContext = {
            changeTrackingId: fileChange && typeof fileChange.trackingId === "number" ? fileChange.trackingId : 0,
            iterationContext: { firstComparingIteration: baseIterationId, secondComparingIteration: currentIterationId }
        } as GitPullRequestCommentThreadContext;
    };

    private isOriginalSide = (threadModel: IGitPullRequestCommentThreadModel): boolean | undefined => {
        if (threadModel.thread.threadContext) {
            return !!threadModel.thread.threadContext.leftFileEnd;
        }
    };
}

const serviceName = "IPullRequestDiscussionIntegrationService";
Services.add(serviceName, { serviceFactory: PullRequestDiscussionIntegrationService });

export function getDiscussionIntegrationService(pageContext: IVssPageContext): IPullRequestDiscussionIntegrationService {
    return pageContext.getService<IPullRequestDiscussionIntegrationService>(serviceName);
}
